import * as THREE from 'three';
import { appModeFromSearch } from '../AppMode';
import { Engine } from '../core/Engine';
import { DevOrbitCameraMode } from '../camera/modes/DevOrbitCameraMode';
import { BallFollowCameraMode } from '../camera/modes/BallFollowCameraMode';
import { CameraModeId } from '../camera/CameraMode';
import { HomeScreen, type HomePanel } from '../ui/HomeScreen';
import { CourseEditor } from '../editor/CourseEditor';
import { ShotSimulation } from '../simulation/ShotSimulation';
import { CourseLibrary, type CourseLibraryEntry } from '../course/CourseLibrary';
import { PlayNowController, type PlayNowConfiguration } from '../playnow/PlayNowController';
import { LocalCareerRepository } from '../career/CareerRepository';
import { INAUGURAL_SEASON } from '../career/SeasonDefinition';
import { QualificationEngine } from '../career/QualificationEngine';
import type { CareerSave } from '../career/types';
import { startNextSeason } from '../career/Progression';
import { CareerTournamentSession } from '../career/CareerTournamentSession';
import { TOUR_GOLFERS } from '../career/TourGolfers';
import type { ShotSimulationConfig } from '../simulation/ShotSimulation';
import { TournamentForecastEngine, formatScore } from '../career/TournamentBroadcast';

export type AppScreen =
  | 'home'
  | 'play-now-library'
  | 'play-now-details'
  | 'gameplay'
  | 'builder-landing'
  | 'builder'
  | 'career-menu'
  | 'new-career'
  | 'career-dashboard'
  | 'season-schedule'
  | 'event-details'
  | 'tournament-leaderboard'
  | 'season-standings'
  | 'career-profile'
  | 'career-history'
  | 'round-summary'
  | 'cut-result'
  | 'season-summary';
interface Mounted {
  dispose(): void | Promise<void>;
}
export interface CareerScreenActions {
  onBack(): void;
  onStartEvent(slot: number, eventId: string): void;
  onResumeTournament(slot: number): void;
  onOpenEvent(slot: number, eventId: string): void;
}

export class AppRouter {
  private mounted: Mounted | null = null;
  private readonly library = new CourseLibrary();
  private readonly careers = new LocalCareerRepository();
  screen: AppScreen = 'home';

  constructor(private readonly container: HTMLElement) {}

  start(): void {
    const mode = appModeFromSearch();
    if (mode === 'builder') this.showBuilderLanding();
    else if (mode === 'career') this.showCareer();
    else if (mode === 'play') this.showCourseSelect();
    else this.showHome();
  }

  private clearCurrent(): void {
    void this.mounted?.dispose();
    this.container.innerHTML = '';
  }

  private replace(create: () => Mounted, screen: AppScreen): void {
    this.clearCurrent();
    this.mounted = create();
    this.screen = screen;
  }

  showHome(panel?: HomePanel): void {
    this.replace(
      () =>
        new HomeScreen(this.container, {
          initialPanel: panel,
          onNavigate: (destination) => {
            if (destination === 'play') this.showCourseSelect();
            else if (destination === 'career') this.showCareer();
            else if (destination === 'builder') this.showBuilderLanding();
            else this.showHome(destination);
          },
        }),
      'home',
    );
  }

  showCourseSelect(): void {
    this.replace(
      () =>
        new CourseSelectScreen(this.container, this.library, {
          onBack: () => this.showHome(),
          onPlay: (e) => void this.startGameplay(e),
        }),
      'play-now-library',
    );
  }
  showCareer(): void {
    this.replace(
      () =>
        new CareerScreen(this.container, this.careers, {
          onBack: () => this.showHome(),
          onStartEvent: (slot, eventId) => void this.startCareerTournament(slot, eventId, false),
          onResumeTournament: (slot) => void this.startCareerTournament(slot, undefined, true),
          onOpenEvent: () => undefined,
        }),
      'career-menu',
    );
  }
  private async startCareerTournament(slot: number, eventId?: string, resume = false) {
    const career = this.careers.loadCareer(slot);
    if (!career) throw new Error('Career slot is empty');
    const event = INAUGURAL_SEASON.events.find(
      (candidate) => candidate.id === (eventId ?? career.activeTournamentState?.eventId),
    );
    if (!event) throw new Error('Career event is unavailable');
    if (!this.library.find(event.courseId)) throw new Error('Tournament course is unavailable');
    const session = new CareerTournamentSession(career, event, this.careers);
    let active = career.activeTournamentState;
    if (!resume) {
      const eligible = new QualificationEngine().evaluate(event.qualificationRule, {
        seasonPointsRank: career.seasonState.standingsRank,
        careerRanking: career.playerProfile.careerRanking,
        priorSeasonPointsRank: 150,
        currentSeasonWins: career.seasonState.results.filter((r) => r.position === 1).length,
        careerWins: career.careerStatistics.wins,
        topTenCount: career.careerStatistics.top10s,
        priorEventFinish: 99,
        defendingChampion: false,
        majorWins: career.careerStatistics.majorWins,
      });
      if (!eligible.eligible || event.scheduleIndex !== career.currentEventIndex)
        throw new Error('Player is not eligible for this event');
      active = session.create();
    }
    if (!active) throw new Error('No tournament checkpoint to resume');
    const opponent = TOUR_GOLFERS.find((golfer) => golfer.id === active.pairedOpponentId);
    const entry = this.library.find(active.courseId);
    if (!entry || !opponent) throw new Error('Tournament session data is unavailable');
    await this.startGameplay(
      entry,
      undefined,
      {
        mode: 'career',
        player: { id: career.playerProfile.id, name: career.playerProfile.name, type: 'human' },
        pairedOpponent: { id: opponent.id, name: opponent.name, type: 'ai' },
        roundNumber: active.playerRound.roundNumber,
        startingHoleNumber: active.playerRound.activeHoleNumber,
        tournamentContext: active,
        tournamentEvent: event,
        careerCallbacks: {
          onStableCheckpoint: (checkpoint) => {
            session.checkpoint('stableShot', checkpoint);
          },
          onHoleCompleted: (result) => {
            session.recordHole(result.playerScore, result.opponentScore, result.par);
            if (career.broadcastSettings.autoOpenPostHole)
              this.container.dispatchEvent(new CustomEvent('career-post-hole-broadcast'));
          },
          onRoundCompleted: () => undefined,
          onLeaderboardRequested: () => undefined,
          onSaveAndExit: (checkpoint) => {
            session.checkpoint('saveAndExit', checkpoint);
          },
        },
      },
      () => {
        session.checkpoint('saveAndExit');
        this.showCareer();
      },
    );
  }
  showBuilderLanding(): void {
    this.replace(
      () =>
        new BuilderLandingScreen(this.container, this.library, {
          onBack: () => this.showHome(),
          onOpen: () => this.openBuilder(),
        }),
      'builder-landing',
    );
  }

  private openBuilder(): void {
    this.clearCurrent();
    const engine = new Engine(this.container);
    const editor = new CourseEditor(engine, this.container, {
      onReturnHome: () => this.showHome(),
    });
    engine.start();
    this.mounted = {
      dispose: () => {
        editor.dispose();
        engine.dispose();
      },
    };
    this.screen = 'builder';
  }

  private async startGameplay(
    entry: CourseLibraryEntry,
    _configuration?: PlayNowConfiguration,
    sessionConfig: ShotSimulationConfig = {},
    onExit?: () => void,
  ): Promise<void> {
    const course = this.library.compile(entry.id);
    if (!course) return;
    this.clearCurrent();
    const engine = new Engine(this.container);
    await course.load(engine);
    const simulation = new ShotSimulation(engine, course, {
      ...sessionConfig,
      debugHudContainer: this.container,
      buildInfo: {
        version: __APP_VERSION__,
        mode: import.meta.env.MODE,
        controlScheme: 'keyboard + mobile swing',
      },
      showDebugTrail: false,
    });
    const openPostHoleBroadcast = () => simulation.openTournamentSheet('leaderboard');
    this.container.addEventListener('career-post-hole-broadcast', openPostHoleBroadcast);
    simulation.start();
    const hole = course.getHole(1);
    const tee = hole?.primaryTee.position ?? new THREE.Vector3();
    const pin = hole?.activePin.position ?? new THREE.Vector3(0, 0, 100);
    const toPin = new THREE.Vector3().subVectors(pin, tee).setY(0).normalize();
    engine.cameras.register(
      new DevOrbitCameraMode(
        engine.renderer.domElement,
        tee.clone().addScaledVector(toPin, 20).setY(1),
        tee
          .clone()
          .addScaledVector(toPin, -14)
          .add(new THREE.Vector3(0, 7, 0)),
      ),
    );
    engine.cameras.register(
      new BallFollowCameraMode(
        () => simulation.activeCameraBall(),
        () => simulation.currentPinPosition(),
        () => simulation.activeAimDirection(),
        () => simulation.landingZonePreviewTarget(),
        () => simulation.isGreenReadEnabled(),
        () => simulation.isPuttingLie(),
      ),
    );
    engine.cameras.setActive(CameraModeId.BallFollow);
    const pause = new PauseMenu(this.container, {
      career: sessionConfig.mode === 'career',
      onExit: () => {
        if (
          confirm(
            sessionConfig.mode === 'career'
              ? 'Save and exit Career?'
              : 'Exit this round and return home?',
          )
        ) {
          if (onExit) onExit();
          else this.showHome();
        }
      },
      onRestartHole: () => simulation.restartHole(),
      onRestartRound: () => simulation.restartRound(),
      onHelp: () => this.showHome('help'),
      onLeaderboard: () => simulation.openTournamentSheet('leaderboard'),
      onScorecard: () => simulation.openTournamentSheet('scorecard'),
      onPauseChanged: (paused) => simulation.setInputSuspended(paused),
    });
    engine.start();
    this.mounted = {
      dispose: () => {
        this.container.removeEventListener('career-post-hole-broadcast', openPostHoleBroadcast);
        pause.dispose();
        simulation.dispose();
        course.unload();
        engine.dispose();
      },
    };
    this.screen = 'gameplay';
  }
}

export class CourseSelectScreen implements Mounted {
  readonly element = document.createElement('main');
  constructor(
    container: HTMLElement,
    library: CourseLibrary,
    actions: { onBack(): void; onPlay(entry: CourseLibraryEntry): void },
  ) {
    const controller = new PlayNowController(library);
    const entries = controller.availableCourses();
    let selected = entries.find((e) => e.validationStatus === 'valid') ?? entries[0];
    this.element.className = 'menu-screen';
    const render = () => {
      this.element.innerHTML = `<style>${menuCss}</style><section class="menu-card"><button class="ghost" data-back>← Home</button><h1>Play Now Course Library</h1><p>Built-in Courses · My Courses</p><div class="course-grid">${entries.map((e) => `<button class="course-tile ${e.id === selected.id ? 'selected' : ''}" data-id="${e.id}"><span class="preview"></span><b>${e.name}</b><small>${e.source === 'built-in' ? 'Built-in' : 'My Course'} • ${e.holeCount} holes • Par ${e.totalPar} • ${e.totalYardage} yd</small><p>${e.description}</p></button>`).join('')}</div><aside class="detail"><h2>${selected.name}</h2><p>${selected.description}</p><div class="settings-grid"><label>Mode <select data-config="opponentMode"><option value="solo">Solo</option><option value="ai" selected>AI Opponent</option></select></label><label>Tee <select data-config="teeSet"><option>Championship</option></select></label><label>Wind <select data-config="windPreset"><option>calm</option><option selected>breezy</option><option>windy</option></select></label><label>Green speed <select data-config="greenSpeed"><option>slow</option><option selected>standard</option><option>fast</option></select></label><label>Holes <select data-config="holes"><option value="front9">Front 9</option><option value="back9">Back 9</option><option value="full18" selected>Full 18</option></select></label><label>AI difficulty <select data-config="aiDifficulty"><option>casual</option><option selected>competitive</option><option>expert</option></select></label><label>Opponent view <select data-config="opponentShotView"><option>watch</option><option selected>condensed</option><option>skip</option></select></label></div><button class="primary" data-play>Start Casual Round</button></aside></section>`;
      bind();
    };
    const bind = () => {
      this.element.querySelector('[data-back]')?.addEventListener('click', actions.onBack);
      this.element
        .querySelector('[data-play]')
        ?.addEventListener('click', () =>
          controller.launch(controller.defaultConfiguration(selected.id), actions.onPlay),
        );
      this.element.querySelectorAll<HTMLButtonElement>('[data-id]').forEach((b) =>
        b.addEventListener('click', () => {
          selected = entries.find((e) => e.id === b.dataset.id) ?? selected;
          if (selected.validationStatus === 'valid') actions.onPlay(selected);
          else render();
        }),
      );
    };
    render();
    container.appendChild(this.element);
  }
  dispose(): void {
    this.element.remove();
  }
}

class BuilderLandingScreen implements Mounted {
  readonly element = document.createElement('main');
  constructor(
    container: HTMLElement,
    library: CourseLibrary,
    actions: { onBack(): void; onOpen(): void },
  ) {
    const saved = library.list().filter((e) => e.source !== 'built-in');
    this.element.className = 'menu-screen';
    this.element.innerHTML = `<style>${menuCss}</style><section class="menu-card narrow"><button class="ghost" data-back>← Home</button><h1>Course Builder</h1><button class="primary" data-open>Create New Course</button><button class="primary" data-open>Continue Last Course</button><button class="secondary" data-open>Edit Saved Course (${saved.length})</button><label class="secondary import">Import Course JSON<input type="file" accept="application/json"></label><p>Saved courses come from the Course Library. Import opens the editor after you choose a JSON file.</p></section>`;
    this.element.querySelector('[data-back]')?.addEventListener('click', actions.onBack);
    this.element
      .querySelectorAll('[data-open]')
      .forEach((b) => b.addEventListener('click', actions.onOpen));
    container.appendChild(this.element);
  }
  dispose(): void {
    this.element.remove();
  }
}

class PauseMenu implements Mounted {
  readonly element = document.createElement('div');
  private readonly onPauseRequested = () => this.open();
  private readonly onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && this.isOpen()) this.close();
  };
  constructor(
    container: HTMLElement,
    actions: {
      career?: boolean;
      onExit(): void;
      onRestartHole(): void;
      onRestartRound(): void;
      onHelp(): void;
      onLeaderboard(): void;
      onScorecard(): void;
      onPauseChanged(paused: boolean): void;
    },
  ) {
    this.element.className = 'pause-menu';
    this.element.innerHTML = `<section class="pause-sheet" role="dialog" aria-modal="true" aria-labelledby="pause-title"><header class="pause-head"><div><small>Gameplay paused</small><h2 id="pause-title">Menu</h2></div><button type="button" data-resume aria-label="Resume game">×</button></header><div class="pause-actions">${actions.career ? '<button type="button" data-leaderboard><span>Leaderboard</span><small>Live tournament field</small></button><button type="button" data-scorecard><span>Scorecard</span><small>Hole-by-hole scores</small></button>' : '<button type="button" data-scorecard><span>Scorecard</span><small>Hole-by-hole scores</small></button><button type="button" data-hole><span>Restart hole</span></button><button type="button" data-round><span>Restart round</span></button>'}<button type="button" data-help><span>Help</span><small>Controls and gameplay</small></button><button type="button" data-exit><span>${actions.career ? 'Save and exit Career' : 'Exit to Home'}</span></button></div></section>`;
    const bind = (selector: string, action: () => void, closeFirst = true) => {
      const button = this.element.querySelector<HTMLButtonElement>(selector);
      if (!button) return;
      const activate = (event: Event) => {
        event.preventDefault();
        event.stopPropagation();
        if (closeFirst) this.close();
        action();
      };
      // Mobile Safari can suppress the synthesized click when the gameplay
      // canvas has touch-action:none. Act on pointerup instead; retain click
      // only for keyboard/assistive activation (detail === 0).
      button.addEventListener('pointerdown', (event) => {
        event.preventDefault();
        event.stopPropagation();
      });
      button.addEventListener('pointerup', activate);
      button.addEventListener('click', (event) => {
        if (event.detail === 0) activate(event);
        else {
          event.preventDefault();
          event.stopPropagation();
        }
      });
    };
    bind('[data-resume]', () => undefined);
    bind('[data-exit]', actions.onExit);
    bind('[data-hole]', actions.onRestartHole);
    bind('[data-round]', actions.onRestartRound);
    bind('[data-help]', actions.onHelp);
    bind('[data-leaderboard]', actions.onLeaderboard);
    bind('[data-scorecard]', actions.onScorecard);
    this.element.addEventListener('pointerdown', (event) => {
      event.stopPropagation();
      if (event.target === this.element) this.close();
    });
    container.addEventListener('gameplay-pause-requested', this.onPauseRequested);
    window.addEventListener('keydown', this.onKeyDown);
    container.appendChild(this.element);
    this.actions = actions;
  }
  private actions: { onPauseChanged(paused: boolean): void };
  private isOpen(): boolean {
    return this.element.classList.contains('open');
  }
  private open(): void {
    if (this.isOpen()) return;
    this.element.classList.add('open');
    this.actions.onPauseChanged(true);
    this.element.querySelector<HTMLButtonElement>('[data-resume]')?.focus();
  }
  private close(): void {
    if (!this.isOpen()) return;
    this.element.classList.remove('open');
    this.actions.onPauseChanged(false);
  }
  dispose(): void {
    this.element.parentElement?.removeEventListener(
      'gameplay-pause-requested',
      this.onPauseRequested,
    );
    window.removeEventListener('keydown', this.onKeyDown);
    this.element.remove();
  }
}

export class CareerScreen implements Mounted {
  readonly element = document.createElement('main');
  private view: 'menu' | 'new' | 'dashboard' | 'schedule' | 'standings' | 'profile' | 'history' =
    'menu';
  private career: CareerSave | null = null;
  constructor(
    container: HTMLElement,
    private readonly repository: LocalCareerRepository,
    private readonly actions: CareerScreenActions,
  ) {
    this.element.className = 'menu-screen';
    container.appendChild(this.element);
    this.render();
  }
  private render() {
    const saves = this.repository.listCareers();
    if (this.view === 'menu') {
      this.element.innerHTML = `<style>${menuCss}</style><section class="menu-card narrow"><button class="ghost" data-home>← Home</button><h1>Career Mode</h1><p>Create or continue a persistent professional-golf career.</p>${[
        1, 2, 3,
      ]
        .map((slot) => {
          const s = saves.find((c) => c.slot === slot);
          return s
            ? `<button class="primary" data-load="${slot}">Slot ${slot}: ${s.playerProfile.name} · Season ${s.currentSeasonNumber}</button>`
            : `<button class="secondary" data-new="${slot}">Slot ${slot}: New Career</button>`;
        })
        .join('')}</section>`;
    } else if (this.view === 'new') {
      this.element.innerHTML = `<style>${menuCss}</style><form class="menu-card narrow" data-form><button type="button" class="ghost" data-menu>← Career Menu</button><h1>New Career</h1><label>Player name<input required name="name" maxlength="32"></label><label>Hometown / region<input name="hometown" maxlength="40"></label><label>Handedness<select name="handedness"><option value="right">Right</option><option value="left">Left</option></select></label><label>Starting difficulty<select name="difficulty"><option value="casual">Casual</option><option value="competitive" selected>Competitive</option><option value="expert">Expert</option></select></label><label>Save slot<select name="slot">${[1, 2, 3].map((n) => `<option ${saves.some((s) => s.slot === n) ? 'disabled' : ''}>${n}</option>`).join('')}</select></label><button class="primary">Begin Career</button></form>`;
    } else {
      const c = this.career!;
      const event = INAUGURAL_SEASON.events[c.currentEventIndex] ?? INAUGURAL_SEASON.events.at(-1)!;
      const qualifier = new QualificationEngine().evaluate(event.qualificationRule, {
        seasonPointsRank: c.seasonState.standingsRank,
        careerRanking: c.playerProfile.careerRanking,
        priorSeasonPointsRank: 150,
        currentSeasonWins: c.seasonState.results.filter((r) => r.position === 1).length,
        careerWins: c.careerStatistics.wins,
        topTenCount: c.seasonState.results.filter((r) => r.position <= 10).length,
        priorEventFinish: 99,
        defendingChampion: false,
        majorWins: c.careerStatistics.majorWins,
        currentSeasonWinEventName: c.seasonState.results.find((r) => r.position === 1)?.eventId,
      });
      const nav = `<nav class="career-nav"><button data-view="dashboard">Dashboard</button><button data-view="schedule">Schedule</button><button data-view="standings">Standings</button><button data-view="profile">Profile</button><button data-view="history">Career History</button></nav>`;
      const eventHistory = c.worldHistory.eventHistory[event.id];
      const forecast =
        c.activeTournamentState?.forecast ??
        new TournamentForecastEngine().generate(
          event,
          TOUR_GOLFERS,
          c.worldHistory,
          c.golferForm,
          c.currentSeasonNumber,
        );
      const golferName = (id: string) =>
        TOUR_GOLFERS.find((golfer) => golfer.id === id)?.name ?? c.playerProfile.name;
      const forecastCards = forecast.favorites
        .slice(0, 5)
        .map(
          (entry) =>
            `<article class="story-card"><b>${entry.forecastRank}. ${golferName(entry.golferId)}</b><small>${entry.reasons.join(' · ') || 'Consistent all-around form'}</small><span>${entry.winProbability}% win outlook</span></article>`,
        )
        .join('');
      let body: string;
      if (this.view === 'schedule')
        body = `<h1>Season Schedule</h1><div class="schedule">${INAUGURAL_SEASON.events.map((e) => `<article><b>${e.scheduleIndex + 1}. ${e.name}</b><span>${e.courseName} · ${e.tier}</span><small>${e.availableCourseStatus === 'comingSoon' ? 'Venue coming in course-content milestone' : 'Available'}</small></article>`).join('')}</div>`;
      else if (this.view === 'standings')
        body = `<h1>Season Standings</h1><p>#${c.seasonState.standingsRank} ${c.playerProfile.name} — ${c.seasonState.points} points</p>`;
      else if (this.view === 'profile')
        body = `<h1>Career Profile</h1><p>${c.playerProfile.name} · ${c.playerProfile.hometown ?? 'Independent'} · ${c.playerProfile.handedness}-handed</p><p>Career ranking #${c.playerProfile.careerRanking}</p><p>Major starts: ${c.careerStatistics.majorStarts} · Major wins: ${c.careerStatistics.majorWins}</p>`;
      else if (this.view === 'history')
        body = `<h1>Tour History</h1><p>An established tour since Season ${c.worldHistory.historyStartSeason}. Your career begins after Season ${c.worldHistory.mostRecentCompletedSeason}.</p><h2>Season Champions</h2><div class="history-list">${c.worldHistory.pastSeasons
          .slice(-8)
          .reverse()
          .map(
            (season) =>
              `<span>Season ${season.season}: <b>${golferName(season.championId)}</b></span>`,
          )
          .join(
            '',
          )}</div><h2>Your Results</h2><p>${c.eventHistory.length ? c.eventHistory.map((r) => `${r.eventId}: #${r.position}`).join('<br>') : 'Your first professional event awaits.'}</p>`;
      else if (c.seasonStatus === 'completed')
        body = `<h1>Season ${c.currentSeasonNumber} Complete</h1><p>Final points rank #${c.seasonState.standingsRank} · ${c.seasonState.points} points</p><button class="primary" data-season-summary>View Season Summary</button><button class="secondary" data-next-season>Start Next Season</button>`;
      else
        body = `<h1>${c.playerProfile.name}</h1><p>Season ${c.currentSeasonNumber} · Points rank #${c.seasonState.standingsRank} · Career rank #${c.playerProfile.careerRanking}</p><div class="event-hero"><small>WEEK ${event.scheduleIndex + 1} · ${event.tier.toUpperCase()}</small><h2 class="${event.tier === 'major' ? 'major-title' : ''}">${event.name}</h2><p>${event.courseName} · ${event.rounds} rounds · ${event.cutRule.enabled ? 'Cut after Round 2' : 'No cut'} · ${event.fieldSize} players</p><p>Greens: ${event.greenSpeedProfile} · Wind ${event.windRange[0]}–${event.windRange[1]} · Purse ${Intl.NumberFormat('en-US', { notation: 'compact', style: 'currency', currency: 'USD' }).format(event.purse)}</p></div><section><h2>Tournament Favorites</h2><div class="story-grid">${forecastCards}</div></section><div class="preview-grid"><section><h2>Defending Champion</h2><b>${golferName(eventHistory.defendingChampionId)} · ${formatScore(eventHistory.pastChampions[0].winningScore)}</b><h3>Recent Champions</h3>${eventHistory.pastChampions
          .slice(0, 4)
          .map((result) => `<p>Season ${result.season}: ${golferName(result.championId)}</p>`)
          .join(
            '',
          )}</section><section><h2>Dark Horses</h2>${forecast.darkHorses.map((entry) => `<p><b>${golferName(entry.golferId)}</b><br><small>${entry.reasons.join(' · ')}</small></p>`).join('') || '<p>Watch the middle of this deep field.</p>'}<h2>Your Outlook: ${forecast.playerForecast.outlook}</h2><p>${forecast.playerForecast.reasons.join(' · ')}</p></section></div><label>Broadcast Frequency<select data-broadcast><option value="full" ${c.broadcastSettings.frequency === 'full' ? 'selected' : ''}>Full</option><option value="balanced" ${c.broadcastSettings.frequency === 'balanced' ? 'selected' : ''}>Balanced</option><option value="minimal" ${c.broadcastSettings.frequency === 'minimal' ? 'selected' : ''}>Minimal</option></select></label><p class="${qualifier.eligible ? 'eligible' : 'locked'}"><b>${qualifier.eligible ? 'Qualified' : 'Locked'}</b> — ${qualifier.reason}</p>${c.activeTournamentState ? `<button class="primary" data-resume>Resume Round ${c.activeTournamentState.playerRound.roundNumber} · Hole ${c.activeTournamentState.playerRound.activeHoleNumber}</button>` : qualifier.eligible ? '<button class="primary" data-play-event>Continue to Round 1</button>' : '<button class="primary" disabled>Qualification required</button>'}<p><b>Rival:</b> Alex Carter · ${c.rivalryState.headline}</p>`;
      this.element.innerHTML = `<style>${menuCss}</style><section class="menu-card"><button class="ghost" data-home>← Return Home</button>${nav}${body}</section>`;
    }
    this.bind();
  }
  private bind() {
    this.element.querySelector('[data-home]')?.addEventListener('click', () => {
      if (this.career) this.repository.saveCareer(this.career);
      this.actions.onBack();
    });
    this.element.querySelector('[data-menu]')?.addEventListener('click', () => {
      this.view = 'menu';
      this.render();
    });
    this.element.querySelectorAll<HTMLElement>('[data-new]').forEach((b) =>
      b.addEventListener('click', () => {
        this.view = 'new';
        this.render();
      }),
    );
    this.element.querySelectorAll<HTMLElement>('[data-load]').forEach((b) =>
      b.addEventListener('click', () => {
        this.career = this.repository.loadCareer(Number(b.dataset.load));
        this.view = 'dashboard';
        this.render();
      }),
    );
    this.element.querySelectorAll<HTMLElement>('[data-view]').forEach((b) =>
      b.addEventListener('click', () => {
        this.view = b.dataset.view as typeof this.view;
        this.render();
      }),
    );
    this.element.querySelector('[data-play-event]')?.addEventListener('click', () => {
      const event = INAUGURAL_SEASON.events[this.career!.currentEventIndex];
      this.actions.onStartEvent(this.career!.slot, event.id);
    });
    this.element
      .querySelector('[data-resume]')
      ?.addEventListener('click', () => this.actions.onResumeTournament(this.career!.slot));
    this.element.querySelector('[data-view-event]')?.addEventListener('click', () => {
      const event = INAUGURAL_SEASON.events[this.career!.currentEventIndex];
      this.actions.onOpenEvent(this.career!.slot, event.id);
    });
    this.element.querySelector('[data-season-summary]')?.addEventListener('click', () => {
      this.view = 'history';
      this.render();
    });
    this.element.querySelector('[data-next-season]')?.addEventListener('click', () => {
      this.career = startNextSeason(this.career!);
      this.repository.saveCareer(this.career);
      this.render();
    });
    this.element
      .querySelector<HTMLSelectElement>('[data-broadcast]')
      ?.addEventListener('change', (event) => {
        this.career!.broadcastSettings.frequency = (event.currentTarget as HTMLSelectElement)
          .value as CareerSave['broadcastSettings']['frequency'];
        this.repository.saveCareer(this.career!);
      });
    this.element.querySelector<HTMLFormElement>('[data-form]')?.addEventListener('submit', (e) => {
      e.preventDefault();
      const f = new FormData(e.currentTarget as HTMLFormElement);
      this.career = this.repository.createCareer(
        {
          name: String(f.get('name')),
          hometown: String(f.get('hometown')) || undefined,
          handedness: f.get('handedness') as 'left' | 'right',
          difficulty: f.get('difficulty') as 'casual' | 'competitive' | 'expert',
        },
        Number(f.get('slot')),
      );
      this.view = 'dashboard';
      this.render();
    });
  }
  dispose() {
    if (this.career) this.repository.saveCareer(this.career);
    this.element.remove();
  }
}

const menuCss = `.menu-screen{height:100svh;overflow:auto;-webkit-overflow-scrolling:touch;touch-action:pan-y;min-height:100svh;padding:calc(18px + env(safe-area-inset-top,0px)) 16px;background:radial-gradient(circle at 50% 0,#245b82,#031321 45%,#000a14);color:#fff;font-family:Inter,system-ui;box-sizing:border-box}.menu-card{max-width:980px;margin:0 auto;padding:18px;border:1px solid rgba(217,178,99,.55);border-radius:24px;background:linear-gradient(180deg,rgba(4,22,41,.92),rgba(1,9,19,.94));box-shadow:0 22px 70px #0008}.menu-card.narrow{max-width:480px;display:grid;gap:12px}.menu-card label{display:grid;gap:5px}.menu-card input,.menu-card select{padding:11px;border-radius:9px;border:1px solid #557da4;background:#071d31;color:white}.ghost,.secondary,.primary{min-height:48px;border-radius:14px;padding:12px 16px;font-weight:800;letter-spacing:.05em}.ghost{background:transparent;color:#fff;border:1px solid #ffffff44}.primary{background:#d9b263;color:#051426;border:0}.secondary{background:#12304c;color:#fff;border:1px solid #557da4}.course-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(230px,1fr));gap:12px}.course-tile{text-align:left;color:white;background:#0d2943;border:1px solid #557da4;border-radius:16px;padding:12px}.course-tile.selected{outline:3px solid #d9b263}.preview{display:block;height:92px;border-radius:12px;background:linear-gradient(145deg,#1b6b8d,#2f6b35 55%,#d9b263 56%,#0e301e)}.course-tile b{display:block;margin-top:10px}.course-tile small{color:#d9e8ff}.detail{margin-top:16px;padding:14px;border-radius:18px;background:#031321cc}.settings-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(160px,1fr));gap:10px;margin:15px 0}.career-nav{display:flex;gap:7px;overflow:auto;margin:12px 0}.career-nav button{padding:10px;background:#12304c;color:white;border:1px solid #557da4;border-radius:10px;white-space:nowrap}.schedule{display:grid;gap:8px}.schedule article{display:grid;padding:12px;border-radius:10px;background:#0d2943}.schedule span,.schedule small{color:#bdd4ed}.eligible{color:#8ce99a}.locked{color:#ffb4a2}.major-title{padding:12px;border-block:1px solid #d9b263;color:#f2d28a;letter-spacing:.08em}.event-hero{padding:18px;border-radius:18px;background:linear-gradient(135deg,#163f61,#07192b);border-left:4px solid #d9b263}.story-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(170px,1fr));gap:9px}.story-card{display:grid;gap:7px;padding:12px;border-radius:12px;background:#0d2943}.story-card small,.story-card span{color:#bdd4ed}.preview-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(240px,1fr));gap:12px;margin:16px 0}.preview-grid section{padding:14px;border-radius:14px;background:#071d31}.history-list{display:grid;gap:7px}.history-list span{padding:9px;background:#0d2943;border-radius:8px}@media (max-width:520px){.menu-screen{padding-inline:10px}.menu-card{padding:14px}}`;
